import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";

import {
  confirmGrantTaskExtraction,
  createGrantTaskExtraction,
  getGrantTaskExtraction,
  GrantTaskExtractionConflictError,
  type GrantTaskExtractionFocus,
  type GrantTaskProposal,
} from "@/api/grant_opportunity_task_extractions";
import type { GrantOpportunityTaskKind } from "@/api/grant_opportunity_tasks";
import { GRANT_TASK_KIND_LABELS } from "@/components/grant_suite/grantSuiteDisplay";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/useToast";
import {
  grantOpportunityEventsQueryKey,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";

const POLL_INTERVAL_MS = 1500;

/** Per-focus copy and kind choices. */
const FOCUS_UI: Record<
  GrantTaskExtractionFocus,
  {
    title: string;
    description: string;
    addedNoun: string;
    kinds: GrantOpportunityTaskKind[];
    stageLabels: Record<string, string>;
  }
> = {
  reporting: {
    title: "Extract reporting requirements",
    description:
      "We scan the document for required reports, attachments, and deadlines. Review each item before it's added to this grant's checklist.",
    addedNoun: "Reporting requirements",
    kinds: ["report", "deliverable"],
    stageLabels: {
      extract_text: "Reading the document…",
      llm_extract: "Finding reporting requirements…",
      llm_extract_retry: "Finding reporting requirements (retrying)…",
      normalize_result: "Cleaning up results…",
    },
  },
  milestone: {
    title: "Generate milestones",
    description:
      "We scan the award letter or agreement for project deadlines and date-bound commitments. Review each milestone before it's added to this grant's checklist.",
    addedNoun: "Milestones",
    kinds: ["general"],
    stageLabels: {
      extract_text: "Reading the document…",
      llm_extract: "Finding milestones…",
      llm_extract_retry: "Finding milestones (retrying)…",
      normalize_result: "Cleaning up results…",
    },
  },
  compliance: {
    title: "Generate compliance checklist",
    description:
      "We scan the document for compliance obligations — certifications, procurement rules, record retention, and other conditions. Review each item before it's added to this grant's checklist.",
    addedNoun: "Compliance items",
    kinds: ["compliance"],
    stageLabels: {
      extract_text: "Reading the document…",
      llm_extract: "Finding compliance requirements…",
      llm_extract_retry: "Finding compliance requirements (retrying)…",
      normalize_result: "Cleaning up results…",
    },
  },
};

type ProposalRow = {
  proposalId: string;
  included: boolean;
  title: string;
  kind: GrantOpportunityTaskKind;
  dueDate: string;
  dueText: string | null;
  sourceQuote: string | null;
};

type GrantTaskExtractionDialogProps = {
  municipalityId: number;
  opportunityId: number;
  document: { id: number; name: string };
  focus?: GrantTaskExtractionFocus;
  onOpenChange: (open: boolean) => void;
};

function proposalToRow(proposal: GrantTaskProposal): ProposalRow {
  return {
    proposalId: proposal.id,
    included: true,
    title: proposal.title,
    kind: proposal.kind,
    dueDate: proposal.due_date ?? "",
    dueText: proposal.due_text,
    sourceQuote: proposal.source_quote,
  };
}

export function GrantTaskExtractionDialog({
  municipalityId,
  opportunityId,
  document,
  focus = "reporting",
  onOpenChange,
}: GrantTaskExtractionDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const focusUi = FOCUS_UI[focus];

  const [extractionId, setExtractionId] = useState<number | null>(null);
  const [rows, setRows] = useState<ProposalRow[] | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createGrantTaskExtraction(municipalityId, opportunityId, {
        document_id: document.id,
        focus,
      }),
    onSuccess: (extraction) => setExtractionId(extraction.id),
  });

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    createMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractionQuery = useQuery({
    queryKey: ["grant_opportunity_task_extraction", municipalityId, opportunityId, extractionId],
    queryFn: () => getGrantTaskExtraction(municipalityId, opportunityId, extractionId as number),
    enabled: extractionId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "processing" ? POLL_INTERVAL_MS : false;
    },
  });

  const extraction = extractionQuery.data;

  useEffect(() => {
    if (extraction?.status === "completed" && extraction.proposals && rows == null) {
      setRows(extraction.proposals.map(proposalToRow));
    }
  }, [extraction, rows]);

  const includedRows = useMemo(() => (rows ?? []).filter((row) => row.included), [rows]);
  const confirmableRows = includedRows.filter((row) => row.title.trim().length > 0);

  const confirmMutation = useMutation({
    mutationFn: () =>
      confirmGrantTaskExtraction(
        municipalityId,
        opportunityId,
        extractionId as number,
        confirmableRows.map((row) => ({
          title: row.title.trim(),
          kind: row.kind,
          due_at: row.dueDate ? new Date(row.dueDate).toISOString() : null,
        })),
      ),
    onSuccess: (tasks) => {
      queryClient.invalidateQueries({
        queryKey: ["grant_opportunity_tasks", municipalityId, opportunityId],
      });
      queryClient.invalidateQueries({
        queryKey: grantOpportunityEventsQueryKey(municipalityId, opportunityId),
      });
      invalidateGrantSuitePipeline(queryClient, municipalityId);
      toast({
        title: `Added ${tasks.length} task${tasks.length === 1 ? "" : "s"}`,
        description: `${focusUi.addedNoun} from “${document.name}” are now on the checklist.`,
      });
      onOpenChange(false);
    },
    onError: (error) => {
      if (error instanceof GrantTaskExtractionConflictError) {
        queryClient.invalidateQueries({
          queryKey: ["grant_opportunity_tasks", municipalityId, opportunityId],
        });
        toast({ title: "Already added", description: error.message });
        onOpenChange(false);
        return;
      }
      toast({
        title: "Could not add tasks",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateRow = (proposalId: string, patch: Partial<ProposalRow>) => {
    setRows((current) =>
      (current ?? []).map((row) => (row.proposalId === proposalId ? { ...row, ...patch } : row)),
    );
  };

  const retry = () => {
    setExtractionId(null);
    setRows(null);
    createMutation.mutate();
  };

  const inFlight =
    createMutation.isPending ||
    extraction?.status === "pending" ||
    extraction?.status === "processing";
  const failed = createMutation.isError || extraction?.status === "failed";
  const failureMessage = createMutation.isError
    ? createMutation.error instanceof Error
      ? createMutation.error.message
      : "Extraction could not be started."
    : extraction?.error_message ?? "Extraction failed. Please try again.";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="ordinal-scrollbar max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {focusUi.title}
          </DialogTitle>
          <DialogDescription>
            {focusUi.description} Source: <span className="font-medium">{document.name}</span>
          </DialogDescription>
        </DialogHeader>

        {inFlight ? (
          <div className="flex items-center gap-3 rounded-lg border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            {(extraction?.current_stage && focusUi.stageLabels[extraction.current_stage]) ||
              "Starting extraction…"}
          </div>
        ) : null}

        {failed ? (
          <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{failureMessage}</p>
            </div>
            <Button variant="outline" size="sm" onClick={retry}>
              Try again
            </Button>
          </div>
        ) : null}

        {rows != null && !inFlight && !failed ? (
          // min-w-0: DialogContent is a grid; without this the nowrap quote line
          // sets the track's min-content width and stretches the dialog instead
          // of truncating.
          <div className="min-w-0 space-y-3">
            <p className="text-xs text-muted-foreground">
              Uncheck anything that shouldn't be tracked. Edit titles, types, and due dates before
              adding.
            </p>
            <ul className="space-y-3">
              {rows.map((row) => (
                <li
                  key={row.proposalId}
                  className={`rounded-lg border p-3 ${row.included ? "" : "opacity-60"}`}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Checkbox
                      checked={row.included}
                      onCheckedChange={(checked) =>
                        updateRow(row.proposalId, { included: checked === true })
                      }
                      aria-label={`Include “${row.title}”`}
                      className="shrink-0"
                    />
                    <Input
                      value={row.title}
                      onChange={(event) => updateRow(row.proposalId, { title: event.target.value })}
                      disabled={!row.included}
                      aria-label="Task title"
                      className="min-w-0 flex-1"
                    />
                    <Select
                      value={row.kind}
                      onValueChange={(kind) =>
                        updateRow(row.proposalId, { kind: kind as GrantOpportunityTaskKind })
                      }
                      disabled={!row.included}
                    >
                      <SelectTrigger className="sm:w-36" aria-label="Type">
                        {/* Radix only learns item labels once the options mount, so render it directly. */}
                        <SelectValue>{GRANT_TASK_KIND_LABELS[row.kind]}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {focusUi.kinds.map((kind) => (
                          <SelectItem key={kind} value={kind}>
                            {GRANT_TASK_KIND_LABELS[kind]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="date"
                      value={row.dueDate}
                      onChange={(event) =>
                        updateRow(row.proposalId, { dueDate: event.target.value })
                      }
                      disabled={!row.included}
                      aria-label="Due date"
                      className="sm:w-40"
                    />
                  </div>
                  {row.sourceQuote || row.dueText ? (
                    // The due text is a substring of the quote in practice, so show one or the other.
                    <p
                      className="mt-2 truncate text-xs text-muted-foreground"
                      title={`Found: “${row.sourceQuote ?? row.dueText}”`}
                    >
                      Found: “{row.sourceQuote ?? row.dueText}”
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                disabled={confirmableRows.length === 0 || confirmMutation.isPending}
                onClick={() => confirmMutation.mutate()}
              >
                {confirmMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : null}
                Add {confirmableRows.length} task{confirmableRows.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
